import React, { useContext, useId, useMemo } from "react"
import { DropzoneOptions, useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { times } from "shared/lib/times"
import { useEvent } from "shared/lib/useEvent"
import { Action } from "shared/types/Action"
import { WithChildren } from "shared/types/WithChildren"
import { WithClassName } from "shared/types/WithClassName"
import { AttachmentType } from "shared/types/__generated__/graphql"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { FileCard } from "shared/ui/file-card/FileCard"
import { Label } from "shared/ui/label/Label"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { Icon } from "../icon/Icon"
import { IconName } from "../icon/__generated__/IconName"
import {
  FileUploadPropsContext,
  FileUploadPropsContextType,
  FileUploadPropsProvider
} from "./FileUploadPropsContext"
import {
  FileUploadStateContext,
  FileUploadStateProvider
} from "./FileUploadStateContext"
import "./styles/_file-input.scss"
import "./styles/_file-upload.scss"

type FileUploadRootPublicProps<Multiple extends boolean> = Omit<
  FileUploadPropsContextType<Multiple>,
  "inputId"
> & { inputId?: string }

interface FileUploadRootProps<Multiple extends boolean>
  extends WithClassName,
    WithChildren,
    FileUploadRootPublicProps<Multiple> {}

function Root<Multiple extends boolean>({
  className,
  children,
  inputId: inputIdProp,
  ...props
}: FileUploadRootProps<Multiple>) {
  const generatedId = useId()
  const inputId = inputIdProp ?? generatedId

  return (
    <FileUploadPropsProvider {...props} inputId={inputId}>
      <FileUploadStateProvider>
        <div className={classNames("file-upload", className)} data-input="">
          {children}
        </div>
      </FileUploadStateProvider>
    </FileUploadPropsProvider>
  )
}

function FileUploadLabel({ className }: WithClassName) {
  const { label, required, inputId } = useContext(FileUploadPropsContext)
  if (!label) return null
  return (
    <Label
      htmlFor={inputId}
      variant="bold"
      className={classNames("file-upload__label", className)}
    >
      {label}
      {required ? "*" : null}
    </Label>
  )
}

function List({ className }: WithClassName) {
  const id = useId()
  const { t } = useTranslation()
  const {
    files,
    isLoading,
    isReadonly,
    loadingFileCount,
    showUploading = true,
    multiple = true
  } = useContext(FileUploadPropsContext)
  const { uploadingFiles, frozenFiles } = useContext(FileUploadStateContext)

  // Normalize files to array for consistent rendering
  const filesArray = React.useMemo(() => {
    if (!files) return []
    return Array.isArray(files) ? files : [files]
  }, [files])

  // Normalize frozen files to array
  const frozenFilesArray = React.useMemo(() => {
    if (frozenFiles === undefined) return undefined
    if (!frozenFiles) return []
    return Array.isArray(frozenFiles) ? frozenFiles : [frozenFiles]
  }, [frozenFiles])

  const displayFiles = frozenFilesArray ?? filesArray

  // In single-file mode during upload, hide existing files
  const shouldShowFiles = multiple || uploadingFiles.length === 0

  if (isLoading) {
    const skeletonCount = loadingFileCount ?? (multiple ? 4 : 1)
    return (
      <div className={classNames("file-upload__list", "loading", className)}>
        {times(skeletonCount, num => (
          <Skeleton
            key={`${id}-loading-file-${num}`}
            containerClassName="file-card file-card--loading"
            flex
          />
        ))}
      </div>
    )
  }

  const hasFiles = displayFiles.length > 0
  const hasUploadingFiles = showUploading && uploadingFiles.length > 0

  if (!hasFiles && !hasUploadingFiles) {
    if (isReadonly) {
      return (
        <div className={classNames("file-upload__list", "empty", className)}>
          <span className="empty-note">{t("Attachments.NoAttachments")}</span>
        </div>
      )
    }
    return null
  }

  return (
    <ul className={classNames("file-upload__list", className)}>
      {shouldShowFiles &&
        displayFiles.map(file => (
          <li key={file.id}>
            <FileUploadItem file={file} />
          </li>
        ))}

      {showUploading &&
        uploadingFiles.map(file => (
          <li key={file.id}>
            <FileUploadUploadingItem file={file} />
          </li>
        ))}
    </ul>
  )
}

interface FileUploadFileInfoProps {
  file: AttachmentType
}

function FileUploadItem({ file }: FileUploadFileInfoProps) {
  const {
    onDelete,
    onReplace,
    isReadonly,
    target,
    FileCardComponent = FileCard
  } = useContext(FileUploadPropsContext)
  const { t } = useTranslation()

  const handleDelete = useEvent(() => {
    return onDelete?.(file)
  })

  const handleReplace = useEvent(() => {
    return onReplace?.(file)
  })

  const actions = useMemo<Action[]>(() => {
    if (isReadonly || !onReplace) return []
    const fullFileName = `${file.name ?? ""}${
      file.extension ? `.${file.extension}` : ""
    }`
    return [
      {
        label: `${fullFileName} ${t("FileInput.Replace")}`,
        icon: IconName.SWAP,
        onClick: handleReplace
      }
    ]
  }, [isReadonly, onReplace, handleReplace, file, t])

  return (
    <FileCardComponent
      attachment={file}
      name={file.name ?? ""}
      extension={file.extension ?? ""}
      url={file.readUrl}
      actions={actions}
      isReadonly={isReadonly}
      onDelete={isReadonly || !onDelete ? undefined : handleDelete}
      target={target}
    />
  )
}

interface FileUploadUploadingItemProps {
  file: {
    id: string
    name: string
    extension: string
    preview: string
    status: "uploading" | "success" | "error"
  }
}

function FileUploadUploadingItem({ file }: FileUploadUploadingItemProps) {
  return (
    <FileCard
      name={file.name}
      extension={file.extension}
      url={file.preview}
      status={file.status}
    />
  )
}

function Input({
  variant = "default",
  className,
  hideDuringUpload = false
}: WithClassName & {
  variant?: "default" | "minimal"
  hideDuringUpload?: boolean
}) {
  const { t } = useTranslation()
  const {
    dataFormats,
    isReadonly,
    isDisabled,
    onUpload,
    multiple = true,
    accept,
    inputId
  } = useContext(FileUploadPropsContext)
  const { addFiles, uploadingFiles } = useContext(FileUploadStateContext)

  const onDrop: DropzoneOptions["onDrop"] = useEvent(acceptedFiles => {
    addFiles(acceptedFiles)
  })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    disabled: isReadonly || isDisabled,
    accept
  })

  if (!onUpload || isReadonly) {
    return null
  }

  if (hideDuringUpload && uploadingFiles.length > 0) {
    return null
  }

  return (
    <div
      {...getRootProps({
        className: classNames(
          "file-upload__input",
          "file-input",
          `file-input--${variant}`,
          {
            "file-input--dragging": isDragActive,
            "file-input--disabled": isDisabled
          },
          className
        )
      })}
      data-drop-zone=""
      data-drop-zone-active={isDragActive ? "" : undefined}
    >
      <div className="file-input__control">
        <input
          {...getInputProps({ className: "file-input__native-input" })}
          id={inputId}
          // react-dropzone renders the input visually hidden with no <label
          // for>, so aria-label is its only accessible name.
          aria-label={t(
            multiple ? "FileInput.ChooseMultiple" : "FileInput.ChooseSingle"
          )}
        />
        <div
          className={classNames("file-input__info", {
            disabled: isReadonly
          })}
        >
          <Icon name={IconName.UPLOAD} className="file-input__icon" />
          <div className="file-input__text">
            <span className="file-input__help-text">
              {t("FileInput.UploadHere")}
            </span>
            {dataFormats && dataFormats.length > 0 ? (
              <div className="file-input__formats">
                {t("FileInput.DataFormat", {
                  format: dataFormats.join(", ")
                })}
              </div>
            ) : null}
          </div>
        </div>
        <Button
          icon={IconName.UPLOAD}
          variant={ButtonVariant.SECONDARY}
          disabled={isReadonly}
          className="file-input__upload-button"
          tabIndex={-1}
          aria-hidden
        >
          {multiple
            ? t("FileInput.ChooseMultiple")
            : t("FileInput.ChooseSingle")}
        </Button>
      </div>
    </div>
  )
}

const FileUpload = {
  Root: React.memo(Root),
  Input: React.memo(Input),
  List: React.memo(List),
  Label: React.memo(FileUploadLabel)
}

export { FileUpload }
